import React, { useMemo } from 'react'
import { makeStyles } from 'tss-react/mui'
import { IconButton, Stack, Typography } from '@mui/material'
import { J1TransferarrowB } from '@e3dc-react/icons'
import { NumberField, TimePicker } from 'src/Libs/inputs/formik'
import useModals from 'src/Hooks/useModals'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { WallboxSettingsFormModel } from 'src/Libs/wallboxes/models'
import { CHARGING_DAYS, Day, dayToIndex } from '@e3dc-react/shell/Libs/date/types'
import { UnitTypes } from 'src/Libs/types'
import ChargeStrategyTotalChargeAmountField from './ChargeStrategyTotalChargeAmountField'
import { useTransferChargingStrategy } from 'src/Libs/wallboxes/hooks'
import { useApp } from 'src/Hooks'
import { useFormikContext } from 'formik'
import { WallboxType } from 'src/Libs/wallboxes/api'

const useStyles = makeStyles()(theme => ({
  container: {
    padding: theme.spacing(2, 4),
    border: `1px solid ${theme.palette.grey.grey06}`,
    borderRadius: theme.shape.borderRadius,
  },
  spaceBetweenRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  iconButton: {
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.primary.main,
    width: theme.spacing(6),
    height: theme.spacing(6),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  fixedWidthField: {
    width: '155px',
  },
  textNumberField: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'space-between',
      gap: theme.spacing(2),
    },
  },
}))

interface Props {
  name: keyof WallboxSettingsFormModel
  weekDayName: Day
  disabled?: boolean
}

/**
 * Component to set Combined charge strategy for a weekday day
 */
const WeekDayCard: React.FC<Props> = ({ name, weekDayName, disabled = false }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { openWallboxWeekDaysDialog } = useModals()
  const { isMobile: isDownSm } = useApp()
  const {
    values: { chargeStrategyConfiguration, wallboxType },
  } = useFormikContext<WallboxSettingsFormModel>()

  const { setSelectedDays } = useTransferChargingStrategy(weekDayName, name)

  /* index to handle backend, where days start with sunday */
  const chargingWeekDayIndex = CHARGING_DAYS.indexOf(weekDayName)
  /* index to handle displaying the week days in normal order starting with monday */
  const weekDayIndex = dayToIndex[weekDayName]

  const unitTypeToDisplay = useMemo((): string => {
    const config = chargeStrategyConfiguration?.find(configuration => configuration.chargeStrategyWeekday === chargingWeekDayIndex)

    // For bidirectional wallboxes with percentage-based charge strategy, display percentage as unit
    if (config?.chargeStrategyUnit === 2 && wallboxType === WallboxType.Bidi) {
      return UnitTypes.percentage
    }

    /* For EDSN wallboxes we always display percentage as unit */
    if (wallboxType === WallboxType.EDSN) {
      return UnitTypes.percentage
    }

    return UnitTypes.kiloWattHour
  }, [chargeStrategyConfiguration, chargingWeekDayIndex, wallboxType])

  const isDecimal = unitTypeToDisplay === UnitTypes.kiloWattHour

  const updateSelectedDays = (selectedDays: Set<string>): void => {
    setSelectedDays(selectedDays)
  }

  const handleClick = (): void => {
    openWallboxWeekDaysDialog({ currentWeekDayIndex: chargingWeekDayIndex, updateSelectedDays })
  }

  return (
    <Stack
      className={classes.container}
      gap={2}
      sx={theme => ({ backgroundColor: disabled ? theme.palette.grey.grey06 : theme.palette.grey.grey07 })}
    >
      <Stack flexDirection="row" justifyContent="space-between">
        <Typography variant="h3">{t(`systemSettings.wallbox.settings.day${weekDayIndex}`)}</Typography>
        <IconButton onClick={handleClick} className={classes.iconButton} disabled={disabled}>
          <J1TransferarrowB />
        </IconButton>
      </Stack>

      <Stack className={classes.spaceBetweenRow} alignItems="center">
        <Typography variant="body1">{t('systemSettings.wallbox.settings.activeChargeStrategy.option.now')}</Typography>
        <Stack className={classes.fixedWidthField}>
          <NumberField
            name={`${name}.${chargingWeekDayIndex}.chargeStrategyDirectChargeAmount`}
            min={0}
            max={isDecimal ? undefined : 100}
            suffix={unitTypeToDisplay}
            disabled={disabled}
            isDecimal={isDecimal}
            step={isDecimal ? 0.01 : 1}
            precision={isDecimal ? 2 : 0}
          />
        </Stack>
      </Stack>

      <Stack className={classes.spaceBetweenRow} alignItems={isDownSm ? 'baseline' : 'center'}>
        <Typography variant="body1">{t('systemSettings.wallbox.settings.activeChargeStrategy.option.delayed')}</Typography>

        <Stack className={classes.textNumberField}>
          <Stack className={classes.fixedWidthField}>
            <TimePicker name={`${name}.${chargingWeekDayIndex}.chargeStrategyDelayedChargeTime`} disabled={disabled} />
          </Stack>
          <Stack className={classes.fixedWidthField}>
            <NumberField
              name={`${name}.${chargingWeekDayIndex}.chargeStrategyDelayedChargeAmount`}
              min={0}
              max={isDecimal ? undefined : 100}
              suffix={unitTypeToDisplay}
              disabled={disabled}
              isDecimal={isDecimal}
              step={isDecimal ? 0.01 : 1}
              precision={isDecimal ? 2 : 0}
            />
          </Stack>
        </Stack>
      </Stack>
      {/* show total amount when using energy values (kWh) */}
      {isDecimal && (
        <Stack className={classes.spaceBetweenRow} alignItems="center">
          <Typography variant="body1">{t('systemSettings.wallbox.settings.activeChargeStrategy.option.total')}</Typography>
          <Stack className={classes.fixedWidthField}>
            <ChargeStrategyTotalChargeAmountField name={name} weekDayName={weekDayName} suffix={unitTypeToDisplay} />
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default WeekDayCard
